// src/components/RoutePlannerCard.jsx
import React, { useEffect, useId, useMemo, useState } from "react";
import { Car, CircleAlert, TriangleAlert } from "lucide-react";

import { trackEvent } from "../lib/analytics";
import { getRouteVerdictMeta } from "../lib/routeVerdictMeta";
import RoutePlannerDetailsModal from "./RoutePlannerDetailsModal";
import AnimatedPill from "./AnimatedPill";
import HazardSourceNote from "./HazardSourceNote";
import { getWarningTypeLabel } from "./routePlannerDetailsHelpers";
import { cautionLevelLabel } from "../lib/cautionLabel";
import { isFeatureAvailable } from "../config/features";
import { useRoutePlanner } from "../hooks/useRoutePlanner";
import { useFreeRecommendation } from "../hooks/useFreeRecommendation";
import { deriveRoutePlannerSummary } from "../lib/routePlannerSummary";
import {
  getHazardBlockText,
  getStayReasonText,
  getHazardWindowNarrative,
  getRoughWeatherWindowText,
} from "../lib/routePlannerNarrative";

// Ticket #436 D3 — same per-day `warnings` the engine already attached to
// each candidate's windowDays (relocationEngine.js's pickExplainDay), the
// SAME array hasHighWarning/hasWarning were computed from. No extra/
// different slicing, no recomputation with different thresholds. If the
// flag is set but no per-day warnings carry that level (shouldn't happen
// given the shared source, but defensive), returns no types — callers show
// severity only, never an invented cause.
function siteCautionReasonTypes(candidateRow, level) {
  const days = Array.isArray(candidateRow?.windowDays) ? candidateRow.windowDays : [];
  const types = new Set();
  for (const d of days) {
    if (!Array.isArray(d?.warnings)) continue;
    for (const w of d.warnings) {
      if (w?.level === level && w?.type) types.add(w.type);
    }
  }
  return [...types];
}

// Ticket #436 correction v2 (F-D): the route-risk badges only showed the
// bare level word ("High"/"Moderate"), ambiguous out of context next to a
// site name and a (possibly also-rendered) site caution badge. This
// combines the existing routeRiskLabel ("Route risk"/"Áhætta á leið") with
// the existing level word so all four call sites (preview/full × HIGH/MED)
// share one label expression instead of risking divergence.
// Ticket #460 — all four call sites below only ever pass the literal
// "HIGH"/"MED" strings today, but this helper must not silently relabel any
// other value as "Low": that's the exact class of bug this ticket fixes
// elsewhere (getRouteRiskSummary, the verdict-block ternary).
function routeRiskBadgeLabel(level, t) {
  const prefix = t("routeRiskLabel") || "Route risk";
  const word =
    level === "HIGH"
      ? t("routeRiskHigh") || "High"
      : level === "MED"
        ? t("routeRiskMed") || "Moderate"
        : level === "LOW"
          ? t("routeRiskLow") || "Low"
          : t("routeRiskUnknown") || "Unknown";
  return `${prefix}: ${word}`;
}

// Map reason type -> FLAT translation key
function reasonTypeToKey(type) {
  switch (type) {
    case "rainStreak":
      return "routeReasonRainStreak";
    case "gust":
      return "routeReasonGust";
    case "wind":
      return "routeReasonWind";
    case "rain":
      return "routeReasonRain";
    case "temp":
      return "routeReasonTemp";
    case "shelter":
      return "routeReasonShelter";
    default:
      return null;
  }
}

function verdictAccentClasses(verdictLower) {
  switch (verdictLower) {
    case "move":
      return "border-emerald-300 dark:border-emerald-700 bg-emerald-50/60 dark:bg-emerald-900/20";
    case "consider":
      return "border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/20";
    case "stay":
    default:
      return "border-sky-300 dark:border-sky-700 bg-sky-50/60 dark:bg-sky-900/20";
  }
}

function verdictIconFromV(v) {
  if (v === "better") return "↑";
  if (v === "worse") return "↓";
  return "•";
}

function ProLock({ t, me, onUpgrade }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm bg-white dark:bg-slate-900">
      <div className="text-base font-bold mb-1">{t("travelAdvisorTitle")}</div>
      <div className="text-sm text-slate-600 dark:text-slate-300 mb-3">
        {t("routePlannerLockedBody")}
      </div>

      <button
        type="button"
        onClick={() => {
          trackEvent("travel_advisor_upgrade_clicked", { source: "pro_lock", userTier: "free" });
          if (typeof onUpgrade === "function") onUpgrade("travel_advisor");
        }}
        className="
          w-full rounded-xl px-3 py-2 text-sm font-semibold
          bg-emerald-600 text-white
          hover:bg-emerald-500
          focus:outline-none focus:ring-2 focus:ring-emerald-400/60
          dark:bg-emerald-500 dark:hover:bg-emerald-400
        "
      >
        {me?.user ? t("proUpgrade") : t("proCtaTitle")}
      </button>
    </div>
  );
}

function FreeUsedLock({ t, me, onUpgrade }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm bg-white dark:bg-slate-900">
      <div className="text-base font-bold mb-1">{t("travelAdvisorTitle")}</div>
      <div className="text-sm text-slate-600 dark:text-slate-300 mb-1">
        {t("freeRecommendationUsed")}
      </div>
      <div className="text-sm text-slate-500 dark:text-slate-400 mb-3">
        {t("freeRecommendationUpgrade")}
      </div>

      <button
        type="button"
        onClick={() => {
          trackEvent("travel_advisor_upgrade_clicked", {
            source: "free_used_lock",
            userTier: "free",
          });
          if (typeof onUpgrade === "function") onUpgrade("travel_advisor");
        }}
        className="
          w-full rounded-xl px-3 py-2 text-sm font-semibold
          bg-emerald-600 text-white
          hover:bg-emerald-500
          focus:outline-none focus:ring-2 focus:ring-emerald-400/60
          dark:bg-emerald-500 dark:hover:bg-emerald-400
        "
      >
        {me?.user ? t("freeRecommendationCta") : t("freeRecommendationProCta")}
      </button>
    </div>
  );
}

function interpolate(template, vars) {
  if (typeof template !== "string") return "";
  let out = template;
  for (const [k, v] of Object.entries(vars || {})) {
    out = out.replaceAll(`{${k}}`, String(v));
  }
  return out;
}

export default function RoutePlannerCard({
  t = (k) => k,
  lang = "en",
  entitlements,
  me,
  onUpgrade,
  sites = [],
  baseSiteId,
  radiusKmDefault = 50,
  windowDaysDefault = 3,
  wetThresholdMmDefault = 3, // eslint-disable-line no-unused-vars
  limitDefault = 30,
  onSummaryChange,
  onSelectSite,
  comparisonState = null,
}) {
  const routeFeature = isFeatureAvailable("bestRoutePlanner", entitlements);
  const isPro = !!routeFeature?.available;
  const isPreview = !!routeFeature?.preview && !isPro;

  const { hasFreeUsed, markFreeUsed } = useFreeRecommendation();

  const [radiusKm, setRadiusKm] = useState(radiusKmDefault);
  const [windowDays, setWindowDays] = useState(windowDaysDefault);
  const [limit] = useState(limitDefault);

  // ✅ Effective values for preview vs pro
  const effectiveRadiusKm = isPreview ? 30 : radiusKm;
  const effectiveWindowDays = isPreview ? 1 : windowDays;
  const effectiveLimit = isPreview ? 1 : limit;

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsCandidate, setDetailsCandidate] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Ticket #436 D2 — one per-instance id base (card and the details modal's
  // portal note can be mounted at once; several rows each carry an expand
  // control, so static ids would collide). D1/D10 — a single composite key
  // ("risk:<siteId>" / "site:<siteId>") tracks which one inline reason
  // disclosure is open, instead of one useState per row.
  const cardInstanceId = useId();
  const [expandedCautionKey, setExpandedCautionKey] = useState(null);

  // UX Miði — supporting-detail disclosure. RoutePlannerCard's own verdict
  // presentation and Upgrade CTAs are downgraded to supporting detail behind
  // this toggle so they never compete with HomeDecisionCard's single primary
  // recommendation/CTA above. Default collapsed. Purely a render-time gate —
  // every hook/computation below (useRoutePlanner, deriveRoutePlannerSummary,
  // useFreeRecommendation/markFreeUsed, onSummaryChange) still runs
  // unconditionally, exactly as before this ticket.
  const [resultsExpanded, setResultsExpanded] = useState(false);

  // ✅ One-time route disclaimer
  const [showRouteDisclaimer, setShowRouteDisclaimer] = useState(false);

  // ✅ Glow state (adaptive radius text)
  const [adaptiveGlow, setAdaptiveGlow] = useState(false);
  const prevAdaptiveUsedRef = React.useRef(null);

  function openDetails(candidateRow) {
    if (isPreview) return;
    if (!candidateRow) return;
    setDetailsCandidate(candidateRow);
    setDetailsOpen(true);
  }

  function getImprovementLabel(candidateRow, tFn) {
    const aggregateType = String(candidateRow?.aggregateType || "same");
    const delta = candidateRow?.deltaVsBase;

    if (aggregateType === "slight") {
      return tFn("routeAggregateSlight");
    }

    if (aggregateType === "better") {
      if (typeof delta !== "number") return tFn("routeImproveBetter");
      if (delta < 1.0) return tFn("routeImproveSlight");
      if (delta < 2.5) return tFn("routeImproveBetter");
      return tFn("routeImproveMuchBetter");
    }

    if (typeof delta !== "number") return null;
    if (delta < 0.1) return tFn("routeImproveNone");

    return tFn("routeDaySame");
  }

  const baseSite = useMemo(() => {
    if (!baseSiteId) return null;
    return (Array.isArray(sites) ? sites : []).find((s) => s?.id === baseSiteId) ?? null;
  }, [baseSiteId, sites]);

  const { loading, error, result, routeRiskData, routeRiskLoading } = useRoutePlanner({
    isPro,
    isPreview,
    baseSiteId,
    baseSite,
    sites,
    effectiveRadiusKm,
    effectiveWindowDays,
    effectiveLimit,
    t,
  });

  // ✅ One-time route disclaimer
  // Gated on resultsExpanded: this is an overlay on top of the card body, so
  // it must not appear while the body is still collapsed behind "Sjá nánar".
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isPro && !isPreview) return;
    if (!resultsExpanded) return;

    const seen = window.localStorage.getItem("campcast_route_disclaimer_seen");
    if (seen !== "true") {
      setShowRouteDisclaimer(true);
    }
  }, [isPro, isPreview, resultsExpanded]);

  // ✅ MUST stay above all early returns (fixes React #310)
  useEffect(() => {
    const usedKm =
      typeof result?.debug?.adaptiveRadiusUsedKm === "number"
        ? result.debug.adaptiveRadiusUsedKm
        : null;

    if (typeof usedKm !== "number") return;

    if (prevAdaptiveUsedRef.current !== null && prevAdaptiveUsedRef.current !== usedKm) {
      setAdaptiveGlow(true);
      const tt = setTimeout(() => setAdaptiveGlow(false), 800);
      return () => clearTimeout(tt);
    }

    prevAdaptiveUsedRef.current = usedKm;
  }, [result]);

  // ✅ MUST stay above all early returns
  const {
    top3,
    best,
    decisionLower,
    summary: routePlannerSummary,
  } = useMemo(() => {
    return deriveRoutePlannerSummary({
      result,
      sites,
      effectiveRadiusKm,
      effectiveWindowDays,
      routeRiskData,
      isPreview,
      isPro,
    });
  }, [result, sites, effectiveRadiusKm, effectiveWindowDays, routeRiskData, isPreview, isPro]);

  // Reconciles the TONE shown on this card with the same metric-comparison
  // override HomeDecisionCard already applies via useComparisonState, so the
  // two cards can never disagree (move here / stay there) for the same
  // candidate. The underlying engine verdict (decisionLower), the candidate
  // itself, and relocationEngine's ranking are untouched — this only changes
  // which of the already-computed tones is rendered. Analytics below
  // intentionally keeps using the raw decisionLower (the true engine
  // verdict), not this display-adjusted value.
  const displayDecisionLower = useMemo(() => {
    if (comparisonState?.showComparison) {
      const { direction } = comparisonState;
      if (direction === "similar" || direction === "current_better") return "stay";
    }
    return decisionLower;
  }, [comparisonState, decisionLower]);

  // Free/Pro presentation gate (Miði 5b): Pro always sees the destination
  // (name/distance/why). Free sees the tone only when the canonical tone is
  // move/consider — STAY stays fully visible for everyone (product rule).
  // This does not change the candidate, the verdict, or any computed field —
  // it only decides whether identity-revealing fields are rendered below.
  const hideDestinationForFree = !isPro && ["move", "consider"].includes(displayDecisionLower);

  // Miði 6: fires once per distinct locked tone (move/consider) actually
  // shown to a Free user — not on every rerender.
  //
  // UX Miði — instrumentation semantic change (GA4 cutover = production
  // deploy date, not this commit date): now additionally gated on
  // resultsExpanded, since the locked destination is no longer visible until
  // "Sjá nánar" is opened. Before this change the event fired on data-load
  // regardless of RoutePlannerCard's own visibility; going forward it fires
  // only once the locked state is actually shown, matching the event's own
  // "actually shown to a Free user" definition.
  const destinationLockedFiredRef = React.useRef(null);
  useEffect(() => {
    if (!resultsExpanded) return;
    if (!hideDestinationForFree) return;
    if (destinationLockedFiredRef.current === displayDecisionLower) return;
    destinationLockedFiredRef.current = displayDecisionLower;
    trackEvent("travel_advisor_destination_locked", {
      recommendation_type: displayDecisionLower,
      userTier: "free",
    });
  }, [resultsExpanded, hideDestinationForFree, displayDecisionLower]);

  const lastDecisionRef = React.useRef(null);
  useEffect(() => {
    if (!decisionLower || decisionLower === lastDecisionRef.current) return;
    lastDecisionRef.current = decisionLower;
    if (decisionLower === "stay") {
      trackEvent("stay_recommended", {
        recommendation: "stay",
        radiusKm: effectiveRadiusKm,
        windowDays: effectiveWindowDays,
      });
    } else if (decisionLower === "move") {
      trackEvent("move_recommended", {
        recommendation: "move",
        radiusKm: effectiveRadiusKm,
        windowDays: effectiveWindowDays,
      });
    }
  }, [decisionLower, effectiveRadiusKm, effectiveWindowDays]);

  const freeUsedFiredRef = React.useRef(false);
  useEffect(() => {
    if (isPro) return;
    if (!result) return;
    markFreeUsed();

    if (freeUsedFiredRef.current) return;
    freeUsedFiredRef.current = true;
    trackEvent("travel_advisor_free_used", {
      recommendation_type: decisionLower,
      userTier: "free",
    });
  }, [isPro, result, markFreeUsed, decisionLower]);

  useEffect(() => {
    if (typeof onSummaryChange !== "function") return;

    if (!result) {
      onSummaryChange(null);
      return;
    }

    onSummaryChange(routePlannerSummary);
  }, [onSummaryChange, result, routePlannerSummary]);

  // UX Miði — supporting-detail disclosure (see resultsExpanded declaration
  // above). Collapsed by default: no verdict, no CTA, nothing that could
  // compete with HomeDecisionCard's single primary recommendation/CTA.
  // Everything below this point — including the Upgrade paywalls — only
  // renders once the user has explicitly opened "Sjá nánar".
  if (!resultsExpanded) {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-base font-bold">
              <span aria-hidden>🧭</span>
              <span>{t("travelAdvisorTeaserTitle")}</span>
            </div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t("travelAdvisorTeaserSubtitle")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setResultsExpanded(true)}
            className="shrink-0 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:border-slate-300 dark:border-slate-700/60 dark:bg-slate-800/30 dark:text-slate-300 dark:hover:bg-slate-700/40"
          >
            {t("travelAdvisorShowDetails")}
          </button>
        </div>
      </div>
    );
  }

  if (!isPro && !isPreview) return <ProLock t={t} me={me} onUpgrade={onUpgrade} />;
  if (!isPro && hasFreeUsed) return <FreeUsedLock t={t} me={me} onUpgrade={onUpgrade} />;

  if (!baseSiteId) {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm bg-white dark:bg-slate-900">
        <div className="text-base font-bold mb-1">{t("travelAdvisorTitle")}</div>
        <div className="text-sm text-slate-600 dark:text-slate-300">
          {t("routePlannerSelectBase")}
        </div>
      </div>
    );
  }

  if (!baseSite && Array.isArray(sites) && sites.length > 0) {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm bg-white dark:bg-slate-900">
        <div className="text-base font-bold mb-1">{t("travelAdvisorTitle")}</div>
        <div className="text-xs text-slate-600 dark:text-slate-300">
          {t("routePlannerBaseLabel")}: <span className="font-semibold">{baseSiteId}</span>
        </div>
        <div className="text-xs text-red-600 mt-2">
          Base site not found (ID mismatch between lists).
        </div>
      </div>
    );
  }

  const isDetailsBestCandidate =
    !!detailsCandidate &&
    !!best &&
    String(detailsCandidate?.siteId || "") === String(best?.siteId || "");

  const THRESH = 0.75;

  function getDayCounts(windowDaysArr, windowDaysCount) {
    const daysArr = Array.isArray(windowDaysArr) ? windowDaysArr : [];
    const slice = typeof windowDaysCount === "number" ? daysArr.slice(0, windowDaysCount) : daysArr;

    let betterDays = 0,
      sameDays = 0,
      worseDays = 0;

    for (const d of slice) {
      const basePts =
        typeof d?.baseSitePoints === "number"
          ? d.baseSitePoints
          : typeof d?.baseSitePointsRaw === "number"
            ? d.baseSitePointsRaw
            : 0;

      const candPts =
        typeof d?.points === "number"
          ? d.points
          : typeof d?.pointsRaw === "number"
            ? d.pointsRaw
            : 0;

      const baseRaw =
        typeof d?.baseSitePointsRaw === "number"
          ? d.baseSitePointsRaw
          : typeof d?.baseSitePoints === "number"
            ? d.baseSitePoints
            : 0;

      const candRaw =
        typeof d?.pointsRaw === "number"
          ? d.pointsRaw
          : typeof d?.points === "number"
            ? d.points
            : 0;

      const deltaPts = candPts - basePts;
      const deltaRaw = candRaw - baseRaw;

      const useRaw =
        (candPts === basePts && (candPts <= 0.0001 || candPts >= 9.9999)) ||
        Math.abs(deltaPts) < 0.0001;

      const delta = useRaw ? deltaRaw : deltaPts;

      if (delta > THRESH) betterDays++;
      else if (delta < -THRESH) worseDays++;
      else sameDays++;
    }

    return { betterDays, sameDays, worseDays, totalDays: slice.length };
  }

  function getVerdictFromDays(windowDaysArr, windowDaysCount) {
    const c = getDayCounts(windowDaysArr, windowDaysCount);
    if (!c || c.totalDays === 0) return "same";

    if (c.betterDays > c.worseDays && c.betterDays >= Math.ceil(c.totalDays / 2)) return "better";
    if (c.worseDays > c.betterDays && c.worseDays >= Math.ceil(c.totalDays / 2)) return "worse";
    return "same";
  }

  function verdictLabelFromV(v) {
    if (v === "better") return t("routeDayBetter");
    if (v === "worse") return t("routeDayWorse");
    return t("routeDaySame");
  }

  function verdictButtonClassFromV(v) {
    if (v === "better") {
      return `
        border border-emerald-200 bg-emerald-50 text-emerald-800
        dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-200
        hover:bg-emerald-100 hover:border-emerald-300
        dark:hover:bg-emerald-900/35 dark:hover:border-emerald-700/50
        focus:ring-emerald-400/50
      `;
    }
    if (v === "worse") {
      return `
        border border-rose-200 bg-rose-50 text-rose-800
        dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200
        hover:bg-rose-100 hover:border-rose-300
        dark:hover:bg-rose-900/40 dark:hover:border-rose-700/50
        focus:ring-rose-400/50
      `;
    }
    return `
      border border-slate-200 bg-slate-50 text-slate-800
      dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200
      hover:bg-slate-100 hover:border-slate-300
      dark:hover:bg-slate-800 dark:hover:border-slate-600/60
      focus:ring-slate-400/50
    `;
  }

  const bestCounts = best
    ? {
        betterDays: best?.betterDays ?? 0,
        sameDays: best?.sameDays ?? 0,
        worseDays: best?.worseDays ?? 0,
        totalDays: Array.isArray(best?.windowDays)
          ? best.windowDays.slice(0, effectiveWindowDays).length
          : effectiveWindowDays,
      }
    : null;

  const meta = result && getRouteVerdictMeta(displayDecisionLower);

  const trendText = (() => {
    const v = displayDecisionLower || "stay";
    if (v === "move") return interpolate(t("routePlannerTrendMove"), { days: effectiveWindowDays });
    if (v === "consider")
      return interpolate(t("routePlannerTrendConsider"), { days: effectiveWindowDays });
    return interpolate(t("routePlannerTrendStay"), { days: effectiveWindowDays });
  })();

  // ---------- Adaptive radius UI text ----------
  const adaptive = result?.debug || null;
  const adaptiveEnabled = !!adaptive?.adaptiveRadiusEnabled;

  const adaptiveMaxKm =
    typeof adaptive?.adaptiveRadiusMaxKm === "number" ? adaptive.adaptiveRadiusMaxKm : null;

  const adaptiveUsedKm =
    typeof adaptive?.adaptiveRadiusUsedKm === "number" ? adaptive.adaptiveRadiusUsedKm : null;

  const adaptiveAttempts = Array.isArray(adaptive?.adaptiveRadiusAttempts)
    ? adaptive.adaptiveRadiusAttempts
    : [];

  const adaptivePrevKm = (() => {
    if (!adaptiveEnabled || typeof adaptiveUsedKm !== "number") return null;
    const idx = adaptiveAttempts.findIndex((a) => a?.radiusKm === adaptiveUsedKm);
    if (idx > 0) return adaptiveAttempts[idx - 1]?.radiusKm ?? null;
    return null;
  })();

  const adaptiveRadiusLine =
    adaptiveEnabled &&
    typeof adaptiveUsedKm === "number" &&
    typeof adaptiveMaxKm === "number" &&
    adaptiveUsedKm < adaptiveMaxKm
      ? interpolate(t("routeAdaptiveRadiusUsed"), { used: adaptiveUsedKm, max: adaptiveMaxKm })
      : "";

  const adaptiveVerdictLine = (() => {
    if (!adaptiveEnabled || typeof adaptiveUsedKm !== "number") return "";

    if (
      (displayDecisionLower === "move" || displayDecisionLower === "consider") &&
      typeof adaptivePrevKm === "number" &&
      adaptiveUsedKm > adaptivePrevKm
    ) {
      return interpolate(t("routeAdaptiveFoundBeyond"), {
        prev: adaptivePrevKm,
        used: adaptiveUsedKm,
      });
    }

    if (displayDecisionLower === "stay" && typeof adaptiveMaxKm === "number") {
      return interpolate(t("routeAdaptiveNoBetterWithin"), {
        used: adaptiveUsedKm,
        max: adaptiveMaxKm,
      });
    }

    return "";
  })();
  // --------------------------------------------

  // ✅ Base hazard context (same day window as the alternatives list / modal window)
  const baseHazardWindowDaysCount = isPreview ? effectiveWindowDays : windowDays;
  const baseExplainDays = Array.isArray(result?.explain?.base?.windowDays)
    ? result.explain.base.windowDays
    : [];
  const baseSliceDays =
    typeof baseHazardWindowDaysCount === "number"
      ? baseExplainDays.slice(0, baseHazardWindowDaysCount)
      : baseExplainDays;

  const baseHasHighWarning = baseSliceDays.some(
    (d) => Array.isArray(d?.warnings) && d.warnings.some((w) => w?.level === "high")
  );
  const baseHasWarning = baseSliceDays.some(
    (d) => Array.isArray(d?.warnings) && d.warnings.some((w) => w?.level === "warn")
  );

  function warningRankFromDays(days) {
    const list = Array.isArray(days) ? days : [];
    let rank = 0;

    for (const d of list) {
      const warnings = Array.isArray(d?.warnings) ? d.warnings : [];
      for (const w of warnings) {
        if (w?.level === "high") rank = Math.max(rank, 2);
        else if (w?.level === "warn") rank = Math.max(rank, 1);
      }
    }

    return rank;
  }

  function getRoughWeatherWindowFromDays(days) {
    const list = Array.isArray(days) ? days : [];

    let bestStart = null;
    let bestEnd = null;
    let bestCount = 0;
    let bestMaxRank = 0;

    let currentStart = null;
    let currentEnd = null;
    let currentCount = 0;
    let currentMaxRank = 0;

    for (const d of list) {
      const warnings = Array.isArray(d?.warnings) ? d.warnings : [];
      const rank = warnings.some((w) => w?.level === "high")
        ? 2
        : warnings.some((w) => w?.level === "warn")
          ? 1
          : 0;

      if (rank >= 1) {
        if (!currentStart) currentStart = d?.date ?? null;
        currentEnd = d?.date ?? null;
        currentCount += 1;
        currentMaxRank = Math.max(currentMaxRank, rank);
      } else if (currentCount > 0) {
        if (
          currentCount > bestCount ||
          (currentCount === bestCount && currentMaxRank > bestMaxRank)
        ) {
          bestStart = currentStart;
          bestEnd = currentEnd;
          bestCount = currentCount;
          bestMaxRank = currentMaxRank;
        }

        currentStart = null;
        currentEnd = null;
        currentCount = 0;
        currentMaxRank = 0;
      }
    }

    if (
      currentCount > 0 &&
      (currentCount > bestCount || (currentCount === bestCount && currentMaxRank > bestMaxRank))
    ) {
      bestStart = currentStart;
      bestEnd = currentEnd;
      bestCount = currentCount;
      bestMaxRank = currentMaxRank;
    }

    return {
      hasWindow: bestCount > 0,
      startDate: bestCount > 0 ? bestStart : null,
      endDate: bestCount > 0 ? bestEnd : null,
      dayCount: bestCount,
      maxSeverity: bestMaxRank >= 2 ? "high" : bestMaxRank >= 1 ? "warn" : "none",
    };
  }

  // Helper: produce a camper-first label when the improvement is mainly hazard reduction
  function hazardFirstLabel(candidateRow) {
    const candHigh = !!candidateRow?.hasHighWarning;
    const candWarn = !!candidateRow?.hasWarning;

    // Base has 🚨 but candidate doesn't => "less severe"
    if (baseHasHighWarning && !candHigh) {
      return t("routeCompareReasonLessSevere") || "Minni hætta";
    }

    // Base has ⚠️ (and no 🚨) but candidate has none => "clearer"
    if (!baseHasHighWarning && baseHasWarning && !candWarn && !candHigh) {
      return t("routeCompareReasonClearer") || "Færri viðvaranir";
    }

    return null;
  }

  // Ticket #436 F8/F10 — compact own-indicator badge with a keyboard/touch
  // reachable reason disclosure (never title-only, never a tooltip/popover/
  // portal — content stays in normal document flow, pushing layout). A
  // plain function returning JSX (not a nested component used as a JSX
  // tag), so it is never treated as a new component type across renders.
  // Shares the card's single `expandedCautionKey` instead of one useState
  // per row, and derives its id from the one per-card `cardInstanceId` (D2).
  function renderInlineCautionBadge({ keyNs, siteId, level, label, reasons, tone }) {
    const compositeKey = `${keyNs}:${siteId}`;
    const expanded = expandedCautionKey === compositeKey;
    const panelId = `${cardInstanceId}-${keyNs}-${siteId}`;
    const Icon = level === "high" ? TriangleAlert : level === "warn" ? CircleAlert : Car;
    const badgeClassName = `inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tone}`;

    // Ticket #436 correction v2 (F-E): a badge with no reasons to disclose
    // must not be a focusable control whose click does nothing — render the
    // same visual badge as a non-interactive span instead (D3's defensive
    // "flag true, no matching per-day data" case lands here).
    if (reasons.length === 0) {
      return (
        <span className="inline-flex flex-col items-start">
          <span className={badgeClassName}>
            <Icon aria-hidden="true" size={11} />
            {label}
          </span>
        </span>
      );
    }

    return (
      <span className="inline-flex flex-col items-start">
        <button
          type="button"
          onClick={() => setExpandedCautionKey((k) => (k === compositeKey ? null : compositeKey))}
          aria-expanded={expanded}
          aria-controls={panelId}
          className={badgeClassName}
        >
          <Icon aria-hidden="true" size={11} />
          {label}
        </button>
        {expanded && (
          <span id={panelId} className="mt-0.5 text-[10px] text-slate-600 dark:text-slate-300">
            {reasons.join(", ")}
          </span>
        )}
      </span>
    );
  }

  const showDecisionReasons =
    !isPreview &&
    ["move", "consider"].includes(String(displayDecisionLower)) &&
    Array.isArray(best?.reasons) &&
    best.reasons.length > 0;

  function getSoftAggregateLabel(candidateRow) {
    if (String(candidateRow?.aggregateType || "same") === "slight") {
      return t("routeAggregateSlight") || "Lítil heildarbæting";
    }

    return null;
  }

  function formatShortDateLabel(dateISO) {
    if (!dateISO) return "";

    try {
      const normalizedLang = String(lang || "").toLowerCase();
      const isIcelandic = normalizedLang === "is" || normalizedLang.startsWith("is-");

      const d = new Date(`${dateISO}T00:00:00Z`);
      const weekday = d.getUTCDay();

      const weekdayShort = isIcelandic
        ? ["Sun", "Mán", "Þri", "Mið", "Fim", "Fös", "Lau"][weekday]
        : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][weekday];

      const day = String(d.getUTCDate()).padStart(2, "0");
      const month = String(d.getUTCMonth() + 1).padStart(2, "0");

      return `${weekdayShort} ${day}/${month}`;
    } catch {
      return String(dateISO).slice(5);
    }
  }

  // Ticket #441: both callers (the main verdict block and the details
  // modal) now pass displayDecisionLower explicitly, so the escape
  // suggestion's eligibility always follows the same canonical card-level
  // gate the headline uses — it can no longer urge relocation while the
  // headline reads "stay". This is a shared gate, not a per-candidate
  // recommendation: candidateRow's own fields (destination, warning ranks,
  // delta qualification, weather window) still describe whichever
  // candidate's details are open, even when that candidate isn't `best`.
  // toneLower's default stays the raw engine verdict only as a fallback for
  // a future caller that forgets to pass it explicitly — any new call site
  // must pass displayDecisionLower, not rely on this default.
  function getEscapeSuggestion(candidateRow, toneLower = decisionLower) {
    if (!candidateRow) return null;
    if (!["move", "consider"].includes(toneLower)) return null;

    const baseRank = warningRankFromDays(baseSliceDays);
    const candidateRank = candidateRow?.hasHighWarning ? 2 : candidateRow?.hasWarning ? 1 : 0;

    const delta = typeof candidateRow?.deltaVsBase === "number" ? candidateRow.deltaVsBase : null;
    const requiredDelta =
      typeof candidateRow?.requiredDelta === "number" ? candidateRow.requiredDelta : null;

    const qualifiesByDelta =
      typeof delta === "number" && typeof requiredDelta === "number"
        ? delta >= requiredDelta
        : typeof delta === "number" && delta > 0;

    if (baseRank < 1) return null;
    if (candidateRank >= baseRank) return null;
    if (!qualifiesByDelta) return null;

    // NEW: do not suggest escape if overall weather is worse
    if (candidateRow?.aggregateType === "worse") return null;

    const cleanSiteName = (candidateRow?.siteName || candidateRow?.siteId || "—")
      .replace(/ campsite$/i, "")
      .trim();
    return {
      title: t("routeEscapeStormTitle") || "🚐 Escape the storm",
      body: t("routeEscapeStormBody") || "Better weather nearby",
      destinationLine: interpolate(t("routeEscapeStormDestination"), {
        km: Number.isFinite(candidateRow?.distanceKm) ? Math.round(candidateRow.distanceKm) : "—",
        site: cleanSiteName,
      }),
      baseWindow: getRoughWeatherWindowFromDays(baseSliceDays),
      candidateRank,
      baseRank,
    };
  }

  function buildRouteNarrative({
    baseSite,
    best,
    shownHazardWindowNarrative,
    bestEscapeSuggestion,
    routeRiskData,
    decisionLower,
    t,
  }) {
    const parts = [];

    if (shownHazardWindowNarrative && baseSite?.name) {
      parts.push(`${shownHazardWindowNarrative} á ${baseSite.name}.`);
    } else if (shownHazardWindowNarrative) {
      parts.push(shownHazardWindowNarrative);
    }

    if (bestEscapeSuggestion?.destinationLine) {
      parts.push(
        interpolate(
          t("routeNarrativeBetterNearby") ||
            "Betri aðstæður gætu verið í nágrenninu: {destination}.",
          {
            destination: bestEscapeSuggestion.destinationLine,
          }
        )
      );
    } else if (
      !hideDestinationForFree &&
      best?.siteName &&
      Number.isFinite(best?.distanceKm) &&
      decisionLower !== "stay"
    ) {
      const destination = interpolate(t("routeEscapeStormDestination"), {
        km: Math.round(best.distanceKm),
        site: best.siteName,
      });
      parts.push(interpolate(t("routeNarrativeBetterNearby"), { destination }));
    }

    if (routeRiskData?.routeRisk === "HIGH") {
      parts.push(
        t("routeNarrativeRouteRiskHigh") || "Aðstæður á leiðinni gætu þó verið erfiðar núna."
      );
    } else if (routeRiskData?.routeRisk === "MED") {
      parts.push(
        t("routeNarrativeRouteRiskMed") || "Aðstæður á leiðinni gætu verið aðeins erfiðar."
      );
    }

    return parts.filter(Boolean);
  }

  const bestHazardBlockText = getHazardBlockText(best, t);
  const bestStayReasonText = getStayReasonText(best, displayDecisionLower, t);
  const bestEscapeSuggestion = hideDestinationForFree
    ? null
    : getEscapeSuggestion(best, displayDecisionLower);
  const bestRoughWeatherWindowText = getRoughWeatherWindowText(
    best,
    t,
    formatShortDateLabel,
    interpolate
  );
  const bestHazardWindowNarrative = getHazardWindowNarrative(best, t);

  const baseHazardWindowNarrative = getHazardWindowNarrative(
    { hazardWindow: result?.explain?.base?.hazardWindow },
    t
  );

  const shownHazardWindowNarrative = bestHazardWindowNarrative || baseHazardWindowNarrative;

  const routeNarrativeLines = buildRouteNarrative({
    baseSite,
    best,
    shownHazardWindowNarrative,
    bestEscapeSuggestion,
    routeRiskData,
    decisionLower: displayDecisionLower,
    t,
  });

  const bestHazardBlockClass =
    best?.hazardBlockMode === "stay"
      ? "text-rose-700 dark:text-rose-300"
      : "text-amber-700 dark:text-amber-300";

  const bestRoughWeatherWindowClass =
    best?.roughWeatherWindow?.maxSeverity === "high"
      ? "text-rose-700 dark:text-rose-300"
      : "text-amber-700 dark:text-amber-300";

  // Ticket #436 D1 — exactly one source note if ANY own site or routeRisk
  // indicator is rendered anywhere in the card: adjacent to the
  // alternatives section when THAT section renders one, otherwise adjacent
  // to the routeRisk verdict-block lines (🚧/🚐, now Car-icon lines) above.
  const altSectionHasIndicator = isPreview
    ? displayDecisionLower !== "stay" &&
      top3.length > 0 &&
      !!best &&
      (routeRiskData?.routeRisk === "HIGH" || routeRiskData?.routeRisk === "MED")
    : top3.some(
        (x) =>
          (x === best &&
            (routeRiskData?.routeRisk === "HIGH" || routeRiskData?.routeRisk === "MED")) ||
          x?.hasHighWarning ||
          x?.hasWarning
      );
  // Ticket #460 J3 — UNKNOWN is neutral information, not a caution; it must
  // not trigger the #436 source note. Only HIGH (any mode) or MED
  // (non-preview) count as an indicator here, same truth table as before
  // for HIGH/LOW, but UNKNOWN no longer silently counts as "!== LOW".
  const verdictBlockHasIndicator =
    routeRiskData?.routeRisk === "HIGH" || (!isPreview && routeRiskData?.routeRisk === "MED");
  const showNoteNearVerdict = !altSectionHasIndicator && verdictBlockHasIndicator;
  const showNoteNearAlternatives = altSectionHasIndicator;

  return (
    <div className="relative rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm bg-white dark:bg-slate-900">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-base font-bold">
            <span aria-hidden>🧭</span>
            <span>{t("travelAdvisorTitle")}</span>
          </div>

          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {t("travelAdvisorSubtitle")}
          </div>

          <div className="mt-1.5 text-sm text-slate-600 dark:text-slate-300">
            {t("routePlannerBaseLabel")}:{" "}
            <span className="font-semibold">{baseSite?.name ?? baseSiteId}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setResultsExpanded(false)}
          className="shrink-0 text-[11px] font-semibold text-slate-400 underline decoration-dotted underline-offset-2 transition-colors hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        >
          {t("travelAdvisorHideDetails")}
        </button>

        {!isPreview && result?.debug?.candidatesScored > 0 && (
          <div className="text-[11px] text-slate-500 dark:text-slate-400 text-right">
            {t("routePlannerAlternativesCount")}: {result.debug.candidatesScored}
          </div>
        )}

        {isPreview && (
          <div className="mt-1 text-right">
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200">
              {t("freeRecommendationBadge")}
            </span>
          </div>
        )}
      </div>

      {/* Controls (Pro only) — collapsed by default */}
      {isPro && (
        <div className="mb-3">
          <button
            type="button"
            aria-expanded={advancedOpen}
            aria-controls="route-planner-advanced"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50/60 text-xs text-slate-600 cursor-pointer transition-colors hover:bg-slate-100 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-300/60 dark:border-slate-700/60 dark:bg-slate-800/30 dark:text-slate-300 dark:hover:bg-slate-700/40 dark:hover:border-slate-600 active:bg-slate-100 dark:active:bg-slate-700/50"
          >
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="text-slate-400 dark:text-slate-500">⚙</span>
              <span className="font-medium">{advancedOpen ? t("routePlannerHideAdvanced") : t("routePlannerShowAdvanced")}</span>
            </span>
            <span className="inline-flex items-center gap-2 text-slate-400 dark:text-slate-500">
              {!advancedOpen && (
                <span className="text-[11px]">{radiusKm} km · {windowDays} {windowDays === 1 ? t("day") : t("days")}</span>
              )}
              <span
                aria-hidden
                className={`text-sm leading-none transition-transform duration-200 ${advancedOpen ? "rotate-180" : ""}`}
              >
                ▼
              </span>
            </span>
          </button>

          {!advancedOpen && (
            <p className="mt-1 px-1 text-[11px] text-slate-400 dark:text-slate-500">
              {t("settingsChangeDistanceWindow")}
            </p>
          )}

          {advancedOpen && (
            <div id="route-planner-advanced" className="mt-2 grid gap-2">
              <label className="text-xs text-slate-600 dark:text-slate-300">
                {t("routePlannerRadius")} ({radiusKm} km)
                <input
                  className="w-full"
                  type="range"
                  min={10}
                  max={400}
                  step={5}
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(Number(e.target.value))}
                />
                {adaptiveRadiusLine ? (
                  <div
                    className={`
                      mt-1 text-[11px] transition-all duration-700
                      ${
                        adaptiveGlow
                          ? "text-emerald-600 dark:text-emerald-400 drop-shadow-[0_0_6px_rgba(16,185,129,0.6)]"
                          : "text-slate-500 dark:text-slate-400"
                      }
                    `}
                  >
                    {adaptiveRadiusLine}
                  </div>
                ) : null}
              </label>

              <label className="text-xs text-slate-600 dark:text-slate-300">
                {t("routePlannerWindowDays")} ({windowDays})
                <input
                  className="w-full"
                  type="range"
                  min={2}
                  max={5}
                  step={1}
                  value={windowDays}
                  onChange={(e) => setWindowDays(Number(e.target.value))}
                />
              </label>
            </div>
          )}
        </div>
      )}

      {loading && <div className="text-xs text-slate-600 dark:text-slate-300">{t("loading")}…</div>}
      {error && <div className="text-xs text-red-600">{error}</div>}

      {!loading && !error && result && meta && (
        <div className="grid gap-3">
          {/* Verdict (Decision Tool) */}
          <div className={`rounded-xl border p-4 ${verdictAccentClasses(displayDecisionLower)}`}>
            <div className="text-xl font-black tracking-tight">
              {displayDecisionLower === "move"
                ? t("routeStateMove")
                : displayDecisionLower === "consider"
                  ? t("routeStateConsider")
                  : t("routeStateStay")}
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              {displayDecisionLower === "move"
                ? t("routeStateMoveDescription")
                : displayDecisionLower === "consider"
                  ? t("routeStateConsiderDescription")
                  : t("routeStateStayDescription")}
            </div>

            {displayDecisionLower === "move" && (
              <ul className="mt-2 pl-4 text-xs grid gap-0.5 list-disc text-slate-700 dark:text-slate-300">
                <li>{t("routePainMoveBulletAvoidWorst")}</li>
                <li>{t("routePainMoveBulletStuckInside")}</li>
                <li>{t("routePainMoveBulletTentCaravan")}</li>
              </ul>
            )}
            {displayDecisionLower === "consider" && (
              <ul className="mt-2 pl-4 text-xs grid gap-0.5 list-disc text-slate-700 dark:text-slate-300">
                <li>{t("routePainConsiderBulletCheckNearby")}</li>
                <li>{t("routePainConsiderBulletLessPleasant")}</li>
              </ul>
            )}
            {displayDecisionLower === "stay" && (
              <ul className="mt-2 pl-4 text-xs grid gap-0.5 list-disc text-slate-700 dark:text-slate-300">
                <li>{t("routePainStayBulletNotEnoughGain")}</li>
                <li>{t("routePainStayBulletNotMissingMuch")}</li>
              </ul>
            )}

            {trendText && (
              <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">{trendText}</div>
            )}

            {routeRiskData?.routeRisk === "HIGH" && (
              <div className="mt-2 flex items-start gap-1 text-xs font-medium text-rose-700 dark:text-rose-300">
                <Car aria-hidden="true" size={13} className="mt-0.5 shrink-0" />
                {t("routeRiskAffectsDecision") ||
                  "Driving conditions may affect this recommendation."}
              </div>
            )}

            {!isPreview && routeRiskLoading && (
              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t("loading")}…</div>
            )}

            {!isPreview &&
              routeRiskData &&
              (routeRiskData.routeRisk === "HIGH" || routeRiskData.routeRisk === "MED") && (
                <div className="mt-2 flex items-start gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                  <Car aria-hidden="true" size={13} className="mt-0.5 shrink-0" />
                  <span>
                    {t("routeRiskLabel") || "Route risk"}:{" "}
                    {routeRiskData.routeRisk === "HIGH"
                      ? t("routeRiskHigh") || "High"
                      : t("routeRiskMed") || "Moderate"}
                    {routeRiskData.coverage?.status === "partial" && (
                      <>
                        {" "}
                        ({t("routeRiskPartialQualifier") || "based on partial route data"})
                      </>
                    )}
                  </span>
                </div>
              )}

            {/* Ticket #460 — UNKNOWN is a neutral information line: no Car
                icon, no amber/caution styling, no #436 source note (see
                verdictBlockHasIndicator above). It must never read as LOW. */}
            {!isPreview && routeRiskData && routeRiskData.routeRisk === "UNKNOWN" && (
              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                {t("routeRiskUnknownDetail") ||
                  "Route-weather data was insufficient to evaluate driving conditions on this route."}
              </div>
            )}

            {showNoteNearVerdict && (
              <div className="mt-2">
                <HazardSourceNote t={t} />
              </div>
            )}

            {bestEscapeSuggestion ? (
              <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2 dark:border-emerald-800/50 dark:bg-emerald-900/20">
                <div className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">
                  {bestEscapeSuggestion.title}
                </div>
                <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">
                  {bestEscapeSuggestion.body}
                </div>
                <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 mt-1">
                  {bestEscapeSuggestion.destinationLine}
                </div>
              </div>
            ) : null}

            {best?.aggregateType === "slight" && (
              <div className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300">
                {t("routeAggregateSlight")}
              </div>
            )}

            {bestHazardBlockText ? (
              <div className={`mt-2 text-xs font-medium ${bestHazardBlockClass}`}>
                {bestHazardBlockText}
              </div>
            ) : null}

            {!bestHazardBlockText && bestRoughWeatherWindowText ? (
              <div className={`mt-2 text-xs font-medium ${bestRoughWeatherWindowClass}`}>
                {bestRoughWeatherWindowText}
              </div>
            ) : null}

            {!bestHazardBlockText && routeNarrativeLines.length > 0 ? (
              <div className="mt-2 rounded-lg border border-slate-200/70 bg-white/60 px-3 py-2 dark:border-slate-700 dark:bg-slate-900/30">
                <div className="space-y-1">
                  {routeNarrativeLines.map((line, idx) => (
                    <div key={idx} className="text-xs text-slate-700 dark:text-slate-300">
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {displayDecisionLower === "stay" && (
              <div className="mt-2 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                {t("routeStayGoodSpot")}
              </div>
            )}

            {bestStayReasonText ? (
              <div className="mt-2 text-xs text-slate-700 dark:text-slate-300">
                {bestStayReasonText}
              </div>
            ) : null}

            {bestCounts && !hideDestinationForFree && (
              <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                {interpolate(t("routeDecisionCounts"), {
                  better: bestCounts.betterDays,
                  same: bestCounts.sameDays,
                  worse: bestCounts.worseDays,
                })}

                {adaptiveVerdictLine ? (
                  <div className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                    {adaptiveVerdictLine}
                  </div>
                ) : null}
              </div>
            )}

            {showDecisionReasons && (
              <ul className="mt-2 pl-4 text-xs grid gap-1 list-disc text-slate-700 marker:text-emerald-500 dark:text-slate-300">
                {best.reasons.slice(0, 3).map((r, idx) => {
                  const key = reasonTypeToKey(r?.type);
                  return key ? <li key={`${r.type}-${idx}`}>{t(key)}</li> : null;
                })}
              </ul>
            )}
          </div>

          {/* Top alternatives / Preview single best */}
          <div>
            <div className="text-sm font-semibold mb-2">
              {isPreview
                ? t("routePlannerBestTomorrow")
                : displayDecisionLower === "stay"
                  ? interpolate(t("routePlannerTopAlternativesNoBetter"), { days: windowDays })
                  : t("routePlannerTopAlternatives")}
            </div>

            {isPreview ? (
              displayDecisionLower !== "stay" && top3.length > 0 ? (
                <ol className="grid gap-2 pl-4 text-xs">
                  {top3.slice(0, 1).map((x) => {
                    const rawV = getVerdictFromDays(x?.windowDays, effectiveWindowDays);
                    const visualV =
                      x === best && routeRiskData?.routeRisk === "HIGH" && rawV === "better"
                        ? "same"
                        : rawV;

                    const triggerKey = `${x.siteId}:${effectiveWindowDays}:${visualV}:${
                      typeof x?.deltaVsBase === "number" ? x.deltaVsBase.toFixed(1) : "na"
                    }`;

                    const hazardLabel = hazardFirstLabel(x);
                    const oldImprovement = getImprovementLabel(x, t);
                    const softAggregateLabel = getSoftAggregateLabel(x);

                    const previewPrimaryLabel =
                      hazardLabel ||
                      softAggregateLabel ||
                      oldImprovement ||
                      verdictLabelFromV(visualV);

                    return (
                      <li key={x.siteId}>
                        <div className="font-semibold flex items-center gap-2 flex-wrap">
                          <span className="flex items-center gap-1.5 flex-wrap">
                            <span>
                              {hideDestinationForFree ? t("nearbyCampsite") : (x.siteName ?? x.siteId)}
                            </span>

                            {x === best &&
                              routeRiskData?.routeRisk === "HIGH" &&
                              renderInlineCautionBadge({
                                keyNs: "risk-preview",
                                siteId: x.siteId,
                                level: "route-high",
                                label: routeRiskBadgeLabel("HIGH", t),
                                reasons: [
                                  t("routeRiskHighTooltip") ||
                                    "Difficult driving conditions along the route",
                                ],
                                tone: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200",
                              })}

                            {x === best &&
                              routeRiskData?.routeRisk === "MED" &&
                              renderInlineCautionBadge({
                                keyNs: "risk-preview",
                                siteId: x.siteId,
                                level: "route-med",
                                label: routeRiskBadgeLabel("MED", t),
                                reasons: [
                                  t("routeRiskMedTooltip") ||
                                    "Some wind-related driving risk along the route",
                                ],
                                tone: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
                              })}
                          </span>

                          {!hideDestinationForFree && Number.isFinite(x?.distanceKm) && (
                            <span className="text-[11px] font-normal text-slate-500 dark:text-slate-400">
                              • {Math.round(x.distanceKm)} km
                            </span>
                          )}

                          <AnimatedPill triggerKey={triggerKey} as="span" className="inline-flex">
                            <button
                              type="button"
                              onClick={() => openDetails(x)}
                              disabled
                              className={`
                                inline-flex items-center gap-1.5
                                rounded-full px-2.5 py-1
                                text-xs font-semibold
                                transition-all duration-150 ease-out
                                focus:outline-none focus:ring-2
                                ${verdictButtonClassFromV(visualV)}
                                cursor-default opacity-90
                              `}
                              title=""
                              aria-label={`${verdictLabelFromV(visualV)}. ${
                                oldImprovement ? `${oldImprovement}. ` : ""
                              }`}
                            >
                              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/60 dark:bg-white/10">
                                {verdictIconFromV(visualV)}
                              </span>
                              <span>{previewPrimaryLabel}</span>
                            </button>
                          </AnimatedPill>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  {t("routePlannerPreviewNoBetter")}
                </div>
              )
            ) : top3.length > 0 ? (
              <ol className="grid gap-2 pl-4 text-xs">
                {top3.map((x) => {
                  const rawV = getVerdictFromDays(x?.windowDays, windowDays);
                  const visualV =
                    x === best && routeRiskData?.routeRisk === "HIGH" && rawV === "better"
                      ? "same"
                      : rawV;

                  const triggerKey = `${x.siteId}:${windowDays}:${visualV}:${
                    typeof x?.deltaVsBase === "number" ? x.deltaVsBase.toFixed(1) : "na"
                  }`;

                  const deltaTitle =
                    typeof x?.deltaVsBase === "number"
                      ? `${t("routeDetailsDelta") || "Delta"}: ${x.deltaVsBase >= 0 ? "+" : ""}${x.deltaVsBase.toFixed(1)}`
                      : "";

                  const oldImprovement = getImprovementLabel(x, t);

                  // ✅ hazard-first label (camper-first)
                  const hazardLabel = hazardFirstLabel(x);

                  // ✅ soft aggregate label from engine
                  const softAggregateLabel = getSoftAggregateLabel(x);

                  const primaryLabel =
                    hazardLabel ||
                    softAggregateLabel ||
                    oldImprovement ||
                    verdictLabelFromV(visualV);

                  return (
                    <li key={x.siteId}>
                      <div className="font-semibold flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          {typeof onSelectSite === "function" ? (
                            <button
                              type="button"
                              onClick={() => onSelectSite(x.siteId)}
                              className="hover:underline cursor-pointer focus:outline-none focus:underline"
                            >
                              {x.siteName ?? x.siteId}
                            </button>
                          ) : (
                            <span>{x.siteName ?? x.siteId}</span>
                          )}

                          {x === best &&
                            routeRiskData?.routeRisk === "HIGH" &&
                            renderInlineCautionBadge({
                              keyNs: "risk",
                              siteId: x.siteId,
                              level: "route-high",
                              label: routeRiskBadgeLabel("HIGH", t),
                              reasons: [
                                t("routeRiskHighTooltip") ||
                                  "Difficult driving conditions along the route",
                              ],
                              tone: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200",
                            })}

                          {x === best &&
                            routeRiskData?.routeRisk === "MED" &&
                            renderInlineCautionBadge({
                              keyNs: "risk",
                              siteId: x.siteId,
                              level: "route-med",
                              label: routeRiskBadgeLabel("MED", t),
                              reasons: [
                                t("routeRiskMedTooltip") ||
                                  "Some wind-related driving risk along the route",
                              ],
                              tone: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
                            })}

                          {x?.hasHighWarning
                            ? renderInlineCautionBadge({
                                keyNs: "site",
                                siteId: x.siteId,
                                level: "high",
                                label: cautionLevelLabel("high", t),
                                reasons: siteCautionReasonTypes(x, "high").map((ty) =>
                                  getWarningTypeLabel(ty, t)
                                ),
                                tone: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-200",
                              })
                            : x?.hasWarning
                              ? renderInlineCautionBadge({
                                  keyNs: "site",
                                  siteId: x.siteId,
                                  level: "warn",
                                  label: cautionLevelLabel("warn", t),
                                  reasons: siteCautionReasonTypes(x, "warn").map((ty) =>
                                    getWarningTypeLabel(ty, t)
                                  ),
                                  tone: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200",
                                })
                              : null}
                        </span>

                        {Number.isFinite(x?.distanceKm) && (
                          <span className="text-[11px] font-normal text-slate-500 dark:text-slate-400">
                            • {Math.round(x.distanceKm)} km
                          </span>
                        )}

                        <AnimatedPill triggerKey={triggerKey} as="span" className="inline-flex">
                          <button
                            type="button"
                            onClick={() => openDetails(x)}
                            className={`
                              inline-flex items-center gap-1.5
                              rounded-full px-2.5 py-1
                              text-xs font-semibold
                              transition-all duration-150 ease-out
                              hover:shadow-sm
                              active:scale-[0.98]
                              focus:outline-none focus:ring-2
                              ${verdictButtonClassFromV(visualV)}
                            `}
                            title={deltaTitle}
                            aria-label={`${primaryLabel}. ${t("routeDetailsOpenHint") || "Open details"}`}
                          >
                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white/60 dark:bg-white/10">
                              {verdictIconFromV(visualV)}
                            </span>
                            <span>{primaryLabel}</span>
                          </button>
                        </AnimatedPill>
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <div className="text-xs text-slate-600 dark:text-slate-300">
                {t("routePlannerNoAlternatives")}
              </div>
            )}

            {showNoteNearAlternatives && (
              <div className="mt-3">
                <HazardSourceNote t={t} />
              </div>
            )}

            {isPreview && (
              <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/60 dark:bg-slate-900/20">
                <div className="text-xs text-slate-700 dark:text-slate-300 mb-2 whitespace-pre-line">
                  {displayDecisionLower === "move"
                    ? t("travelAdvisorMoveCtaBody")
                    : displayDecisionLower === "consider"
                      ? t("travelAdvisorConsiderCtaBody")
                      : t("routePlannerPreviewBody")}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    trackEvent("travel_advisor_upgrade_clicked", {
                      source: "preview_cta",
                      recommendation_type: displayDecisionLower,
                      userTier: "free",
                    });
                    if (typeof onUpgrade === "function") onUpgrade("travel_advisor");
                  }}
                  className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {displayDecisionLower === "move"
                    ? t("travelAdvisorMoveCta")
                    : displayDecisionLower === "consider"
                      ? t("travelAdvisorConsiderCta")
                      : t("proUpgrade")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showRouteDisclaimer && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/85 dark:bg-slate-950/85 backdrop-blur-[2px]">
          <div className="mx-3 w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-4">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {t("routeDisclaimerTitle")}
            </div>

            <div className="mt-2 space-y-2 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
              <p>{t("routeDisclaimerBody1")}</p>
              <p>{t("routeDisclaimerBody2")}</p>
              <p>{t("routeDisclaimerBody3")}</p>
            </div>

            <button
              type="button"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.localStorage.setItem("campcast_route_disclaimer_seen", "true");
                }
                setShowRouteDisclaimer(false);
              }}
              className="
                mt-4 w-full rounded-xl px-3 py-2 text-sm font-semibold
                bg-emerald-600 text-white
                hover:bg-emerald-500
                focus:outline-none focus:ring-2 focus:ring-emerald-400/60
                dark:bg-emerald-500 dark:hover:bg-emerald-400
              "
            >
              {t("routeDisclaimerConfirm")}
            </button>
          </div>
        </div>
      )}

      <RoutePlannerDetailsModal
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        t={t}
        lang={lang}
        baseSiteLabel={baseSite?.name ?? baseSiteId}
        candidate={detailsCandidate}
        windowDaysCount={effectiveWindowDays}
        adaptiveUsedKm={adaptiveUsedKm}
        adaptiveMaxKm={adaptiveMaxKm}
        escapeSuggestion={
          detailsCandidate ? getEscapeSuggestion(detailsCandidate, displayDecisionLower) : null
        }
        routeRiskData={routeRiskData}
        showRouteRisk={isDetailsBestCandidate}
      />
    </div>
  );
}
